import { useContext, useEffect, useState, FormEvent } from 'react';
import '../../style/selects.css';
import Translatable from '../translatable_text/Translatable';
import { useSelector } from '../../store/Store';
import { AuthContext } from '../../context/authContext';
import { AxiosError } from 'axios';
import { validateLoginField } from '@/hooks/validate-field';
import MsgComponent from '../msg_component/MsgComponent';
import { useNavigate } from 'react-router-dom';

function LoginForm() {

  const rtl = useSelector(( state ) => state.customizer.activeDir === 'rtl');
  const { login } = useContext(AuthContext);  
  const [ err, setErr ] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate()

  const sanitize = (value: string) => {
    if (typeof value !== 'string') return value;
    return value.replace(/[\u0000-\u001F\u007F]/g, '')
      .replace(/[<>]/g, '')
      .trim();
  };

  // Real-time validation handler
  const handleFieldChange = (fieldName: string, value: string) => {
    const validation = validateLoginField(fieldName, value);
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: validation.isValid ? '' : (validation.message || '')
    }));
  };
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setErr('')
    }, 4000);
  
    // Cleanup function: Clear the timeout when the component unmounts or err changes
    return () => {
      clearTimeout(timer);
    };
    
  }, [err])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setIsLoading(true);
    setErr('');

    try {
      // Enhanced validation for all fields
      const formData = new FormData(form);
      let hasErrors = false;
      const errors: string[] = [];

      // Validate email and password
      const email = formData.get('email') as string || '';
      const password = formData.get('password') as string || '';

      const emailValidation = validateLoginField('email', email);
      const passwordValidation = validateLoginField('password', password);

      if (!emailValidation.isValid) {
        hasErrors = true;
        errors.push(emailValidation.message || 'Email is invalid');
        setFieldErrors(prev => ({ ...prev, email: emailValidation.message || '' }));
      } else {
        setFieldErrors(prev => ({ ...prev, email: '' }));
      }

      if (!passwordValidation.isValid) {
        hasErrors = true;
        errors.push(passwordValidation.message || 'Password is invalid');
        setFieldErrors(prev => ({ ...prev, password: passwordValidation.message || '' }));
      } else {
        setFieldErrors(prev => ({ ...prev, password: '' }));
      }

      // Check native form validity
      if (!form.checkValidity()) {
        hasErrors = true;
      }

      if (hasErrors) {
        form.reportValidity();
        setIsLoading(false);
        return;
      }

      // Sanitize inputs
      const sanitizedEmail = sanitize(email.toLowerCase()).slice(0, 100);
      formData.set('email', sanitizedEmail);

      await login(formData);

      navigate('/admin/dashboard')
      
    } catch (error) {
      if (error instanceof AxiosError) {
        setErr(error.response?.data?.message || error.response?.data?.error || 'Login failed');
        console.log('Login error:', error);
      } else {
        setErr('An unexpected error occurred during login');
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <section className="contact section-padding sub-bg padded-container">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-7">
            <div className="full-width">
              <div className="sec-head mb-50">
                <h3 className="text-u ls1"> GCAD <span className="fw-200"> Admin </span> Auth </h3>
                <span style={{ color: 'gray' }} className="mt-10"><Translatable>Access the admin dashboard</Translatable></span>
              </div>
              <form id="application-form" onSubmit={handleSubmit} noValidate>
                <div className="controls row">

                  <div className="col-lg-12">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="email" className="mb-10">
                          <Translatable>
                            Email
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        placeholder={rtl ? "ادخل ايميلك الشخصي" : "name@example.com"}
                        required
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        className={fieldErrors.email ? 'error' : ''}
                      />
                      {fieldErrors.email && (
                        <span className="error-message">{fieldErrors.email}</span>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-12">
                    <div className="form-group mb-30">
                      <div className='flex gap-1'>
                        <label htmlFor="password" className="mb-10">
                          <Translatable>
                            Password
                          </Translatable>
                        </label>
                        <div><span className='required-dot'>*</span></div>
                      </div>
                      <input
                        id="password"
                        type="password"
                        name="password"
                        placeholder={rtl ? "ادخل كلمة المرور" : "Your password"}
                        required
                        onChange={(e) => handleFieldChange('password', e.target.value)}
                        className={fieldErrors.password ? 'error' : ''}
                      />
                      {fieldErrors.password && (
                        <span className="error-message">{fieldErrors.password}</span>
                      )}
                    </div>
                  </div>

                  <MsgComponent message={err} className="error-msg text-red-800"/>

                  <div className="col-12">
                    <div className="mt-30">
                      <button 
                        type="submit" 
                        className="butn butn-full butn-bord radius-30"
                        disabled={isLoading}
                      >
                        <span className="text">
                          <Translatable>
                            {isLoading ? 'Signing in...' : 'Sign In'}
                          </Translatable>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LoginForm;
